import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { FileText, Store, Lock, ScrollText, ClipboardCheck } from 'lucide-react';
import { Button } from '../../../../ui/Button';
import { OrientationSelector } from './OrientationSelector';
import { VisitVoucherModal, VisitVoucherData } from './VisitVoucherModal';

interface PrintMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onPrint: (type: 'showcase' | 'client' | 'private' | 'contract' | 'visit-voucher', orientation?: 'portrait' | 'landscape', visitData?: VisitVoucherData) => void;
}

const PrintMenu: React.FC<PrintMenuProps> = ({ isOpen, onClose, onPrint }) => {
  const [showOrientationSelector, setShowOrientationSelector] = useState(false);
  const [showVisitVoucherModal, setShowVisitVoucherModal] = useState(false);
  const [selectedType, setSelectedType] = useState<'showcase' | 'client' | 'private' | 'contract'>('showcase');

  console.log('🖨️ PrintMenu rendu:', { isOpen, showOrientationSelector, showVisitVoucherModal });

  if (!isOpen) return null;

  const handlePrint = (type: 'showcase' | 'client' | 'private' | 'contract' | 'visit-voucher') => {
    console.log('🖨️ PrintMenu handlePrint appelé avec type:', type);
    
    if (type === 'showcase') {
      console.log('📄 Type showcase - affichage sélecteur orientation');
      setSelectedType(type);
      setShowOrientationSelector(true);
    } else if (type === 'visit-voucher') {
      console.log('📄 Type visit-voucher - affichage modale bon de visite');
      setShowVisitVoucherModal(true);
    } else {
      console.log('📄 Type', type, '- génération directe');
      onPrint(type, 'portrait');
      onClose();
    }
  };

  const handleVisitVoucherConfirm = (data: VisitVoucherData) => {
    console.log('📄 Données bon de visite confirmées:', data);
    onPrint('visit-voucher', 'portrait', data);
    setShowVisitVoucherModal(false);
    onClose();
  };

  const handleModalClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const modalContent = (
    <div 
      className="fixed inset-0 flex items-center justify-center bg-black/50" 
      style={{ zIndex: 99999 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      {showVisitVoucherModal ? (
        <VisitVoucherModal
          isOpen={showVisitVoucherModal}
          onClose={() => {
            setShowVisitVoucherModal(false);
            onClose();
          }}
          onConfirm={handleVisitVoucherConfirm}
        />
      ) : showOrientationSelector ? (
        <OrientationSelector 
          onSelect={(orientation) => {
            console.log('📐 Orientation sélectionnée:', orientation, 'pour type:', selectedType);
            onPrint(selectedType, orientation);
            setShowOrientationSelector(false);
            onClose();
          }}
          onCancel={() => {
            console.log('❌ Sélection orientation annulée');
            setShowOrientationSelector(false);
            onClose();
          }}
        />
      ) : (
        <div 
          className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800"
          onClick={handleModalClick}
        >
          <h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
            Sélectionnez un document à imprimer
          </h3>
          <div className="space-y-3">
            <button
              onClick={(e) => {
                handleModalClick(e);
                handlePrint('showcase');
              }}
              className="flex w-full items-start gap-3 rounded-lg p-3 text-left transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <div className="rounded-lg bg-primary-500/10 p-2">
                <Store className="h-5 w-5 text-primary-500" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">
                  Fiche Vitrine
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Logo, ville, photo principale, prix, titre et texte vitrine
                </p>
              </div>
            </button>

            <button
              onClick={(e) => {
                handleModalClick(e);
                handlePrint('client');
              }}
              className="flex w-full items-start gap-3 rounded-lg p-3 text-left transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <div className="rounded-lg bg-primary-500/10 p-2">
                <FileText className="h-5 w-5 text-primary-500" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">
                  Fiche Client
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Informations détaillées du bien sans données confidentielles
                </p>
              </div>
            </button>

            <button
              onClick={(e) => {
                handleModalClick(e);
                handlePrint('private');
              }}
              className="flex w-full items-start gap-3 rounded-lg p-3 text-left transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <div className="rounded-lg bg-primary-500/10 p-2">
                <Lock className="h-5 w-5 text-primary-500" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">
                  Fiche Privée
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Toutes les informations incluant les données confidentielles du vendeur
                </p>
              </div>
            </button>

            <button
              onClick={(e) => {
                handleModalClick(e);
                handlePrint('visit-voucher');
              }}
              className="flex w-full items-start gap-3 rounded-lg p-3 text-left transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <div className="rounded-lg bg-primary-500/10 p-2">
                <ClipboardCheck className="h-5 w-5 text-primary-500" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">
                  Bon de Visite
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Document officiel pour les visites conforme à la loi algérienne
                </p>
              </div>
            </button>

            <button
              onClick={(e) => {
                handleModalClick(e);
                handlePrint('contract');
              }}
              className="flex w-full items-start gap-3 rounded-lg p-3 text-left transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <div className="rounded-lg bg-primary-500/10 p-2">
                <ScrollText className="h-5 w-5 text-primary-500" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">
                  Contrat
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Mandat de vente exclusif entre l'agence et le propriétaire
                </p>
              </div>
            </button>
          </div>
          <div className="mt-6 flex justify-end" onClick={handleModalClick}>
            <Button 
              variant="secondary" 
              onClick={(e) => {
                handleModalClick(e);
                onClose();
              }}
            >
              Fermer
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default PrintMenu;
